import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Button,
    CircularProgress,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Card,
    CardContent,
    Grid,
    Paper,
    Tooltip
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Assessment as AssessmentIcon,
    AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { apiClient } from '@/services/api';
import { showSuccessMessage } from '@/components/common/GlobalSnackbar';

interface ProjectLog {
    id: number;
    title: string;
    log_type: string;
    source?: string;
    log_date: string;
    content: string;
    created_at: string;
    updated_at?: string;
}

const LogPanel: React.FC = () => {
    const { projectId: projectIdStr } = useParams<{ projectId: string }>();
    const projectId = projectIdStr ? parseInt(projectIdStr, 10) : null;

    const [logs, setLogs] = useState<ProjectLog[]>([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentLog, setCurrentLog] = useState<Partial<ProjectLog>>({});
    const [isEditing, setIsEditing] = useState(false);

    // 提取日志相关状态
    const [extractConfirmDialogOpen, setExtractConfirmDialogOpen] = useState(false);
    const [extractingLog, setExtractingLog] = useState(false);
    const [logExtractionTaskId, setLogExtractionTaskId] = useState<string | null>(null);
    const logExtractionPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [extractDialogError, setExtractDialogError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);

    // 数据分析相关状态
    const [analyzingFunds, setAnalyzingFunds] = useState(false);
    const [fundsTaskId, setFundsTaskId] = useState<string | null>(null);
    const [fundsError, setFundsError] = useState<string | null>(null);
    const fundsPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // 知识图谱状态
    const [kgStatus, setKgStatus] = useState<{
        status: string;
        progress: number;
        total_documents: number;
        processed_documents: number;
    } | null>(null);

    const fetchLogs = async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const response = await apiClient.projectFacts.getList(projectId);
            if (response.success) {
                // Handle both array and object response (legacy vs new)
                let logsData = Array.isArray(response.data) ? response.data :
                    (response.data?.logs ? response.data.logs : []);

                // 排序：主日志（writing类型）排在第一位，其他按创建时间倒序
                logsData = logsData.sort((a: ProjectLog, b: ProjectLog) => {
                    const aIsWriting = a.log_type === 'writing';
                    const bIsWriting = b.log_type === 'writing';

                    // 如果一个是writing，另一个不是，writing排在前面
                    if (aIsWriting && !bIsWriting) return -1;
                    if (!aIsWriting && bIsWriting) return 1;

                    // 如果都是writing或都不是writing，按创建时间倒序
                    const aTime = new Date(a.created_at || 0).getTime();
                    const bTime = new Date(b.created_at || 0).getTime();
                    return bTime - aTime;
                });

                setLogs(logsData);
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();

        // 首次加载时获取一次知识图谱状态（以防DocumentPanel还未加载）
        const fetchKgStatusOnce = async () => {
            if (!projectId) return;
            try {
                const response = await apiClient.knowledgeGraph.getStatus(projectId.toString());
                if (response.success) {
                    setKgStatus(response.data);
                }
            } catch (error) {
            }
        };
        fetchKgStatusOnce();

        const handleRefresh = () => {
            fetchLogs();
        };

        // 监听知识图谱状态更新事件（由DocumentPanel发送，避免重复调用API）
        const handleKgStatusUpdate = (event: CustomEvent) => {
            setKgStatus(event.detail);
        };

        window.addEventListener('projectFactsShouldRefresh', handleRefresh);
        window.addEventListener('kgStatusUpdated', handleKgStatusUpdate as EventListener);

        return () => {
            window.removeEventListener('projectFactsShouldRefresh', handleRefresh);
            window.removeEventListener('kgStatusUpdated', handleKgStatusUpdate as EventListener);
        };
    }, [projectId]);

    const handleDelete = async (id: number) => {
        if (!projectId) return;
        if (!window.confirm('确定要删除此事实吗？')) return;

        try {
            await apiClient.projectFacts.delete(projectId, id);
            fetchLogs();
        } catch (error) {
        }
    };

    const handleSave = async () => {
        if (!projectId) return;

        // 清除之前的错误
        setSaveError(null);

        // 检查主日志唯一性
        if (currentLog.log_type === 'writing') {
            const existingWritingLog = logs.find(log => {
                // 如果是编辑模式，排除当前正在编辑的日志
                if (isEditing && currentLog.id && log.id === currentLog.id) {
                    return false;
                }
                return log.log_type === 'writing';
            });

            if (existingWritingLog) {
                setSaveError('项目中已存在主日志，每个项目只能有一个主日志。请先删除或修改现有的主日志。');
                return;
            }
        }

        try {
            // 确保手动创建/编辑的日志来源为"手动创建"
            const logData = {
                ...currentLog,
                source: '手动创建'
            };

            if (isEditing && currentLog.id) {
                await apiClient.projectFacts.update(projectId, currentLog.id, logData);
            } else {
                await apiClient.projectFacts.create(projectId, logData);
            }
            setDialogOpen(false);
            setSaveError(null);
            fetchLogs();
        } catch (error) {
            setSaveError('保存失败，请稍后重试。');
        }
    };

    const openCreateDialog = () => {
        setCurrentLog({
            title: '',
            log_type: 'writing',
            content: '',
            log_date: new Date().toISOString().split('T')[0]
        });
        setIsEditing(false);
        setSaveError(null);
        setDialogOpen(true);
    };

    const openEditDialog = (log: ProjectLog) => {
        setCurrentLog(log);
        setIsEditing(true);
        setSaveError(null);
        setDialogOpen(true);
    };

    const getLogTypeLabel = (type: string) => {
        const types: { [key: string]: string } = {
            'writing': '主日志',
            'general': '通用',
            'overview': '概览',
            'fund': '资金',
            'process': '过程',
            'benefit': '效益',
        };
        return types[type] || type;
    };

    const getLogTypeColor = (type: string) => {
        const colorMap: { [key: string]: { bgcolor: string; color: string; borderColor: string } } = {
            'writing': { bgcolor: '#1976d2', color: '#fff', borderColor: '#1976d2' }, // 蓝色 - 主日志
            'general': { bgcolor: '#757575', color: '#fff', borderColor: '#757575' }, // 灰色 - 通用
            'overview': { bgcolor: '#7b1fa2', color: '#fff', borderColor: '#7b1fa2' }, // 紫色 - 概览
            'fund': { bgcolor: '#f57c00', color: '#fff', borderColor: '#f57c00' }, // 橙色 - 资金
            'process': { bgcolor: '#388e3c', color: '#fff', borderColor: '#388e3c' }, // 绿色 - 过程
            'benefit': { bgcolor: '#c2185b', color: '#fff', borderColor: '#c2185b' }, // 粉色 - 效益
        };
        return colorMap[type] || { bgcolor: '#9e9e9e', color: '#fff', borderColor: '#9e9e9e' }; // 默认灰色
    };

    const getSourceLabel = (source?: string) => {
        if (!source) return '手动创建';
        const sourceMap: { [key: string]: string } = {
            'AI探索': 'AI探索',
            'AI总结': 'AI总结',
            '必须手动创建': '手动创建',
            '数据分析': 'AI分析'
        };
        // If not in map, return source as is
        return sourceMap[source] || source;
    };

    // 检查是否有正在执行的日志提取任务
    const checkRunningLogExtractionTask = useCallback(async (): Promise<boolean> => {
        if (!projectId) return false;

        try {
            const response = await apiClient.documentTasks.getStatus(projectId.toString());

            if (response.success && response.data?.tasks) {
                // 查找 log_extraction 类型的任务
                const logExtractionTasks = response.data.tasks.filter((t: any) =>
                    t.task_type === 'log_extraction'
                );

                // 检查是否有正在执行的任务（running 或 pending）
                const runningTask = logExtractionTasks.find((t: any) =>
                    t.status === 'running' || t.status === 'pending'
                );

                if (runningTask) {
                    return true; // 有正在执行的任务
                }
            }
            return false; // 没有正在执行的任务
        } catch (err: any) {
            return false; // 出错时允许继续
        }
    }, [projectId]);

    const handleAiGenerate = () => {
        if (!projectId) return;

        // 立即显示确认对话框，不检查任务
        setExtractDialogError(null);
        setExtractConfirmDialogOpen(true);
    };

    const handleExtractConfirmed = async () => {
        if (!projectId) return;

        // 在确认时检查是否有正在执行的任务
        const hasRunningTask = await checkRunningLogExtractionTask();

        if (hasRunningTask) {
            // 在对话框中显示错误提示，不关闭对话框
            setExtractDialogError('已有日志提取任务正在执行，请等待任务完成后再试');
            return;
        }

        // 清除对话框错误
        setExtractDialogError(null);
        setExtractingLog(true);
        setError(null);
        setExtractConfirmDialogOpen(false);
        setLogExtractionTaskId(null);

        try {
            const response = await apiClient.documents.extractLog(projectId.toString());

            if (response.success && response.data?.task_id) {
                setError(null);
                setLogExtractionTaskId(response.data.task_id);
                // 开始轮询任务状态
                startLogExtractionPolling(response.data.task_id);
            } else {
                setError(response.message || '创建项目日志提取任务失败');
                setExtractingLog(false);
            }
        } catch (err: any) {
            setError(err.response?.data?.detail || '创建项目日志提取任务时发生错误');
            setExtractingLog(false);
        }
    };

    // 开始轮询日志提取任务状态
    const startLogExtractionPolling = useCallback((taskId: string) => {
        // 清除之前的轮询
        if (logExtractionPollingIntervalRef.current) {
            clearInterval(logExtractionPollingIntervalRef.current);
        }

        // 立即检查一次
        checkLogExtractionTaskStatus(taskId);

        // 每2秒轮询一次
        const interval = setInterval(() => {
            checkLogExtractionTaskStatus(taskId);
        }, 2000);

        logExtractionPollingIntervalRef.current = interval;
    }, [projectId]);

    // 检查日志提取任务状态
    const checkLogExtractionTaskStatus = useCallback(async (taskId: string) => {
        if (!projectId) return;

        try {
            const response = await apiClient.documentTasks.getStatus(projectId.toString());

            if (response.success && response.data?.tasks) {
                // 查找对应的任务
                const task = response.data.tasks.find((t: any) => t.task_id === taskId);

                if (task) {
                    if (task.status === 'completed') {
                        // 任务完成，停止轮询
                        if (logExtractionPollingIntervalRef.current) {
                            clearInterval(logExtractionPollingIntervalRef.current);
                            logExtractionPollingIntervalRef.current = null;
                        }
                        setExtractingLog(false);
                        setLogExtractionTaskId(null);

                        // 刷新日志列表
                        fetchLogs();

                        // 显示成功提示
                        showSuccessMessage('项目日志提取并保存成功！');
                    } else if (task.status === 'failed') {
                        // 任务失败，停止轮询
                        if (logExtractionPollingIntervalRef.current) {
                            clearInterval(logExtractionPollingIntervalRef.current);
                            logExtractionPollingIntervalRef.current = null;
                        }
                        setExtractingLog(false);
                        setLogExtractionTaskId(null);
                        setError(task.error_message || '项目日志提取失败');
                    }
                    // running 和 pending 状态继续轮询
                } else {
                    // 任务不存在，可能是刚创建还未同步，继续轮询
                }
            }
        } catch (err: any) {
            // 不显示错误，继续轮询
        }
    }, [projectId]);

    // 数据分析相关代码 =============================================

    // 启动数据分析
    const handleFundsAnalysis = async () => {
        if (!projectId) return;

        // 检查是否已有任务正在运行
        const hasRunning = await checkRunningFundsTask();
        if (hasRunning) {
            setFundsError('已有数据分析任务正在执行，请等待任务完成后再试');
            return;
        }

        setFundsError(null);
        setAnalyzingFunds(true);
        setFundsTaskId(null);

        try {
            // 调用业务汇总报告生成 API
            const response = await apiClient.businessSummary.generate(projectId);

            if (response.success && response.data?.task_id) {
                setFundsTaskId(response.data.task_id);
                startFundsPolling(response.data.task_id);
            } else {
                setFundsError(response.message || '创建数据分析任务失败');
                setAnalyzingFunds(false);
            }
        } catch (err: any) {
            setFundsError(err.response?.data?.detail || '创建数据分析任务时发生错误');
            setAnalyzingFunds(false);
        }
    };

    // 检查数据分析任务
    const checkRunningFundsTask = useCallback(async (): Promise<boolean> => {
        if (!projectId) return false;
        try {
            const response = await apiClient.documentTasks.getStatus(projectId.toString());
            if (response.success && response.data?.tasks) {
                const fundsTasks = response.data.tasks.filter((t: any) =>
                    t.task_type === 'business_summary'
                );
                const runningTask = fundsTasks.find((t: any) =>
                    t.status === 'running' || t.status === 'pending'
                );
                return !!runningTask;
            }
            return false;
        } catch (err: any) {
            return false;
        }
    }, [projectId]);

    // 轮询数据分析
    const startFundsPolling = useCallback((taskId: string) => {
        if (fundsPollingIntervalRef.current) {
            clearInterval(fundsPollingIntervalRef.current);
        }
        checkFundsTaskStatus(taskId);
        const interval = setInterval(() => {
            checkFundsTaskStatus(taskId);
        }, 2000);
        fundsPollingIntervalRef.current = interval;
    }, [projectId]);

    const checkFundsTaskStatus = useCallback(async (taskId: string) => {
        if (!projectId) return;
        try {
            const response = await apiClient.documentTasks.getStatus(projectId.toString());
            if (response.success && response.data?.tasks) {
                const task = response.data.tasks.find((t: any) => t.task_id === taskId);
                if (task) {
                    if (task.status === 'completed') {
                        if (fundsPollingIntervalRef.current) {
                            clearInterval(fundsPollingIntervalRef.current);
                            fundsPollingIntervalRef.current = null;
                        }
                        setAnalyzingFunds(false);
                        setFundsTaskId(null);
                        fetchLogs();
                        showSuccessMessage('数据分析报告生成成功！');
                    } else if (task.status === 'failed') {
                        if (fundsPollingIntervalRef.current) {
                            clearInterval(fundsPollingIntervalRef.current);
                            fundsPollingIntervalRef.current = null;
                        }
                        setAnalyzingFunds(false);
                        setFundsTaskId(null);
                        setFundsError(task.error_message || '数据分析失败');
                    }
                }
            }
        } catch (err: any) {
        }
    }, [projectId]);

    // 组件卸载时清除轮询
    useEffect(() => {
        return () => {
            if (logExtractionPollingIntervalRef.current) {
                clearInterval(logExtractionPollingIntervalRef.current);
            }
            if (fundsPollingIntervalRef.current) {
                clearInterval(fundsPollingIntervalRef.current);
            }
        };
    }, []);

    // ==========================================================

    // 格式化时间显示
    const formatTimeAgo = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return '今天';
        if (diffDays === 1) return '1天前';
        if (diffDays < 7) return `${diffDays}天前`;
        if (diffDays < 14) return '1周前';
        if (diffDays < 21) return '2周前';
        if (diffDays < 30) return '3周前';
        return `${Math.floor(diffDays / 30)}个月前`;
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderLeft: 1, borderColor: 'divider', bgcolor: '#f5f5f5' }}>
            {/* 标题 */}
            <Box sx={{ p: 3, bgcolor: 'white', borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center' }}>
                    调研成果
                </Typography>
            </Box>

            {/* 分析类别卡片区域 */}
            <Box sx={{ p: 3, bgcolor: 'white', borderBottom: 1, borderColor: 'divider' }}>
                <Grid container spacing={2}>
                    {/* 总结日志 - AI事实提取 */}
                    <Grid item xs={6}>
                        <Tooltip
                            title={kgStatus?.status !== 'completed' ? '请等待知识图谱构建完成后再使用此功能' : ''}
                            arrow
                        >
                            <Card
                                sx={{
                                    cursor: kgStatus?.status === 'completed' ? 'pointer' : 'not-allowed',
                                    transition: 'all 0.2s',
                                    opacity: kgStatus?.status === 'completed' ? 1 : 0.6,
                                    '&:hover': kgStatus?.status === 'completed' ? {
                                        boxShadow: 3,
                                        transform: 'translateY(-2px)'
                                    } : {},
                                    height: '100%'
                                }}
                                onClick={kgStatus?.status === 'completed' ? handleAiGenerate : undefined}
                            >
                                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2.5 }}>
                                    <Box sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 2,
                                        bgcolor: '#e3f2fd',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <AssessmentIcon sx={{ fontSize: 28, color: '#1976d2' }} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            总结日志
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            AI事实提取
                                        </Typography>
                                    </Box>
                                    {extractingLog || logExtractionTaskId ? (
                                        <CircularProgress size={20} />
                                    ) : null}
                                </CardContent>
                            </Card>
                        </Tooltip>
                    </Grid>

                    {/* 数据分析 */}
                    <Grid item xs={6}>
                        <Card
                            sx={{
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    boxShadow: 3,
                                    transform: 'translateY(-2px)'
                                },
                                height: '100%'
                            }}
                            onClick={handleFundsAnalysis}
                        >
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2.5 }}>
                                <Box sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 2,
                                    bgcolor: '#f3e5f5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <AccountBalanceIcon sx={{ fontSize: 28, color: '#7b1fa2' }} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        数据分析
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        AI智能统计
                                    </Typography>
                                </Box>
                                {analyzingFunds || fundsTaskId ? (
                                    <CircularProgress size={20} />
                                ) : null}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* 项目日志列表区域 */}
            <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : logs.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="body2">暂无日志</Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {logs.map((log) => (
                            <Paper
                                key={log.id}
                                elevation={0}
                                sx={{
                                    p: 2,
                                    bgcolor: 'white',
                                    borderRadius: 2,
                                    border: '1px solid #e0e0e0',
                                    '&:hover': {
                                        boxShadow: 2
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                            {log.title || '未命名日志'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatTimeAgo(log.updated_at || log.created_at)}
                                            </Typography>
                                            <Tooltip
                                                title={log.log_type === 'writing' ? '一个项目只能有一个“主日志”，点击“总结日志”按钮会自动覆盖现在日志列表中的“主日志”。' : ''}
                                                arrow
                                            >
                                                <Chip
                                                    label={getLogTypeLabel(log.log_type)}
                                                    size="small"
                                                    style={{
                                                        backgroundColor: getLogTypeColor(log.log_type).bgcolor,
                                                        color: getLogTypeColor(log.log_type).color,
                                                        border: `1px solid ${getLogTypeColor(log.log_type).borderColor}`,
                                                        height: 20,
                                                        fontSize: '0.7rem',
                                                        fontWeight: 500
                                                    }}
                                                    sx={{
                                                        '& .MuiChip-label': {
                                                            px: 1,
                                                            color: getLogTypeColor(log.log_type).color
                                                        },
                                                        '&:hover': {
                                                            opacity: 0.9
                                                        }
                                                    }}
                                                />
                                            </Tooltip>
                                            <Chip
                                                label={getSourceLabel(log.source)}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.7rem',
                                                    borderColor: '#d0d0d0',
                                                    color: '#666',
                                                    '& .MuiChip-label': {
                                                        px: 1
                                                    }
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            {log.content || '无内容'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.5, ml: 2 }}>
                                        <IconButton size="small" onClick={() => openEditDialog(log)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(log.id)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                )}
            </Box>

            {/* 底部手动添加按钮 */}
            <Box sx={{ p: 2, bgcolor: 'white', borderTop: 1, borderColor: 'divider' }}>
                <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={openCreateDialog}
                    sx={{ textTransform: 'none' }}
                >
                    手动添加
                </Button>
            </Box>

            {/* 错误提示 */}
            {error && (
                <Box sx={{ p: 2 }}>
                    <Alert severity="error" onClose={() => setError(null)}>
                        {error}
                    </Alert>
                </Box>
            )}

            {fundsError && (
                <Box sx={{ p: 2 }}>
                    <Alert severity="error" onClose={() => setFundsError(null)}>
                        {fundsError}
                    </Alert>
                </Box>
            )}

            {/* 提取日志确认对话框 */}
            <Dialog
                open={extractConfirmDialogOpen}
                onClose={() => {
                    setExtractConfirmDialogOpen(false);
                    setExtractDialogError(null);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>确认提取项目日志</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        提取日志将会覆盖已有的"主日志"类型日志，是否继续？
                    </Alert>
                    {extractDialogError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {extractDialogError}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setExtractConfirmDialogOpen(false);
                        setExtractDialogError(null);
                    }}>
                        取消
                    </Button>
                    <Button
                        onClick={handleExtractConfirmed}
                        variant="contained"
                        color="primary"
                        disabled={extractingLog}
                    >
                        {extractingLog ? '提取中...' : '确认提取'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 编辑/新建事实对话框 */}
            <Dialog open={dialogOpen} onClose={() => {
                setDialogOpen(false);
                setSaveError(null);
            }} maxWidth="md" fullWidth>
                <DialogTitle>{isEditing ? '编辑事实' : '新建事实'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {saveError && (
                            <Alert severity="error" onClose={() => setSaveError(null)}>
                                {saveError}
                            </Alert>
                        )}
                        <TextField
                            label="标题"
                            fullWidth
                            value={currentLog.title || ''}
                            onChange={(e) => setCurrentLog({ ...currentLog, title: e.target.value })}
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel>类型</InputLabel>
                                <Select
                                    value={currentLog.log_type || 'writing'}
                                    label="类型"
                                    onChange={(e) => setCurrentLog({ ...currentLog, log_type: e.target.value })}
                                >
                                    <MenuItem value="writing">主日志</MenuItem>
                                    <MenuItem value="general">通用</MenuItem>
                                    <MenuItem value="overview">概览</MenuItem>
                                    <MenuItem value="fund">资金</MenuItem>
                                    <MenuItem value="process">过程</MenuItem>
                                    <MenuItem value="benefit">效益</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label="日志时间"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={currentLog.log_date || ''}
                                onChange={(e) => setCurrentLog({ ...currentLog, log_date: e.target.value })}
                            />
                        </Box>
                        <TextField
                            label="事实内容"
                            multiline
                            rows={10}
                            fullWidth
                            value={currentLog.content || ''}
                            onChange={(e) => setCurrentLog({ ...currentLog, content: e.target.value })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>取消</Button>
                    <Button onClick={handleSave} variant="contained">保存</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LogPanel;
